import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { ACTION } from '@atlaskit/editor-common/analytics/types/enums';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';

import type { AIPlugin } from '../../../aiPluginType';
import { aiExperienceDecorationPluginKey } from '../../../pm-plugins/decoration/decoration-plugin-key';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import type {
	FireAnalyticsStep,
	StepName,
	UserFlowStepAttributes,
} from '../../utils/analytics/analyticsFlowTypes';
import { useAnalyticsFlow } from '../../utils/analytics/analyticsFlowUtils';
import { rangeCharWordCount, useFireAIAnalyticsEvent } from '../../utils/analytics/utils';
import { getStepName } from '../../utils/error-handling';
import { FAILURE_REASON } from '../../utils/errors';
import { getSelectionCategory, getSelectionNodeTypes } from '../../utils/selection';

/**
 * Ref type for bridging the React-based AnalyticsFlow to the pure-pm plugin.
 * The ref is held in the plugin factory closure (aiPlugin) and set by
 * AnalyticsBridge via api.aiExperience.actions.setAnalyticsBridgeRef.
 * The pure-pm plugin receives a getter and calls it when firing steps.
 */
export type AnalyticsFlowBridgeRef = {
	current: {
		fireAnalyticsStep: FireAnalyticsStep;
	} | null;
};

/**
 * Stable identifier surfaced as `callerComponent` on `noEventExistsToBeFired`
 * analytics errors originating from this bridge, so they can be attributed in
 * Databricks rather than showing `callerComponent: undefined`.
 */
const CALLER_COMPONENT = 'AnalyticsBridge';

/**
 * Bridges analytics data from ProseMirror plugin state into the userFlow
 * analytics flow.
 *
 * On the pure-pm path, streaming operations happen in the ProseMirror plugin
 * without React involvement. This component sits between
 * AnalyticsFlowContextProvider and RenderScreen, giving it a stable lifecycle
 * that spans all screen transitions. This allows it to:
 *
 * - Accumulate state across screen changes (e.g., durations from loading
 *   through to accept/reject)
 * - Observe ProseMirror state changes continuously
 * - Sync data from the plugin world into the analytics flow
 */
export function AnalyticsBridge({
	api,
	children,
	uiState,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	children: React.ReactNode;
	uiState: string;
}): React.JSX.Element {
	const analyticsFlow = useAnalyticsFlow();
	const lastQueuedStep = useRef<StepName | undefined>();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const intl = useIntl();
	// Track whether the viewed event has already been fired for this session
	// to prevent double-firing on re-renders.
	const hasViewedFired = useRef(false);

	const { editorView, canInlineStreamChanges } = useContentComponentContext();

	// Read decoration state synchronously so that common attributes are
	// available before the first analytics step fires. The hook-based values
	// serve as a reactive fallback for later updates.
	const decorationState = editorView?.state
		? aiExperienceDecorationPluginKey.getState(editorView.state)
		: undefined;

	const {
		traceId,
		promptVersion,
		configItem: hookConfigItem,
		initialPositions: hookInitialPositions,
		sessionStatus,
		errorInfo,
	} = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
		traceId: state.aiExperienceState?.aiDecorationExperiencePluginState.traceId,
		promptVersion: state.aiExperienceState?.aiDecorationExperiencePluginState.promptVersion,
		configItem: state.aiExperienceState?.aiDecorationExperiencePluginState.configItem,
		initialPositions: state.aiExperienceState?.aiDecorationExperiencePluginState.initialPositions,
		sessionStatus: state.aiExperienceState?.aiSessionPluginState?.status,
		errorInfo: state.aiExperienceState?.aiSessionPluginState?.errorInfo,
	}));

	const configItem = decorationState?.configItem ?? hookConfigItem;
	const initialPositions = decorationState?.initialPositions ?? hookInitialPositions;

	const isStreaming = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) =>
		Boolean(state.aiExperienceState?.isStreaming),
	);

	const isIdle = useRef(true);

	// Guards against duplicate prompt analytics fires across re-renders.
	const lastFiredSessionStatus = useRef<string | undefined>();

	// Holds a step queued by the ProseMirror plugin whose firing is deferred to a
	// useEffect (see below). Declared here so the error effect can consume/clear it
	// and avoid firing a stale step against an already-flushed queue.
	const pendingStepRef = useRef<{
		attributes?: Partial<UserFlowStepAttributes>;
		step: StepName;
		targetStep?: StepName;
	} | null>(null);

	// Track time spent in loading vs streaming for duration measurements.
	// These persist across screen transitions since this component doesn't unmount.
	const loadingStartTime = useRef(performance.now());
	const streamingStartTime = useRef<number>();

	if (!streamingStartTime.current && isStreaming) {
		streamingStartTime.current = performance.now();
	}

	useEffect(() => {
		if (uiState === 'idle' && !isIdle.current) {
			loadingStartTime.current = performance.now();
			isIdle.current = true;
			hasViewedFired.current = false;
			lastFiredSessionStatus.current = undefined;
			// Reset the dedup guard so the next session can re-fire the same step
			// (e.g. `invoke`) instead of having it silently dropped.
			lastQueuedStep.current = undefined;
		} else {
			isIdle.current = false;
		}
	}, [uiState]);

	// The analyticsFlow instance is recreated per session (keyed on aiSessionId in
	// SessionUIComponent). When it changes, reset the dedup guard so a step already
	// fired against the previous flow can fire again against the new one.
	useEffect(() => {
		lastQueuedStep.current = undefined;
	}, [analyticsFlow]);

	// Record prompt status; actual analytics fire in the pendingStep effect
	// (the analyticsFlow is recreated when aiSessionId changes, so we cannot
	// enqueue here directly).
	useEffect(() => {
		if (sessionStatus === 'prompt' && lastFiredSessionStatus.current !== 'prompt') {
			lastFiredSessionStatus.current = 'prompt';
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionStatus]);

	// Fire error analytics (apiError or failed) when entering error status.
	// The analyticsFlow may be recreated between sessions (with a fresh invoke
	// queued), so we flush any queued step first, then enqueue and fire the error.
	useEffect(() => {
		if (sessionStatus !== 'error' || !errorInfo || lastFiredSessionStatus.current === 'error') {
			return;
		}

		lastFiredSessionStatus.current = 'error';

		// Consume any step the plugin queued but that the deferred pendingStep
		// effect has not yet processed. If we left it in place, that effect would
		// later run against the queue this error path is about to flush, firing a
		// spurious `noEventExistsToBeFired` error. We take over responsibility for
		// flushing it here and reset the dedup guard so real subsequent steps can
		// still be queued.
		pendingStepRef.current = null;
		lastQueuedStep.current = undefined;

		const stepName = getStepName(errorInfo);

		// Flush any previously queued step (e.g. invoke from a recreated flow).
		// Only do this when a step is actually queued: firing against an empty queue
		// emits a spurious `noEventExistsToBeFired` operational error, which is the
		// common case here because `invoke` has usually already fired by the time an
		// error occurs (leaving the recreated flow's queue empty).
		if (analyticsFlow.hasQueuedStep()) {
			analyticsFlow.fireQueuedStep(stepName, CALLER_COMPONENT);
		}

		analyticsFlow.enqueueStep(
			{
				stepName,
				attributes: {
					errorType: errorInfo.failureReason ?? FAILURE_REASON.UNKNOWN,
					sawAupViolation: errorInfo.failureReason === FAILURE_REASON.AUP_VIOLATION,
					...errorInfo,
				},
			},
			CALLER_COMPONENT,
		);
		analyticsFlow.fireQueuedStep(undefined, CALLER_COMPONENT);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionStatus, errorInfo]);

	useEffect(() => {
		const streamingStart = streamingStartTime.current;
		const loadingStart = loadingStartTime.current;

		return () => {
			if (!streamingStart) {
				return;
			}

			analyticsFlow.addAttributes({
				durations: {
					timeSpentWithoutPartialPreview: streamingStart - loadingStart,
					timeSpentWithPartialPreview: performance.now() - streamingStart,
				},
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sync traceId and promptVersion into common attributes when it becomes available from the backend
	useEffect(() => {
		if (traceId || promptVersion) {
			analyticsFlow.updateCommonAttributes({
				traceIds: traceId ? [traceId] : undefined,
				promptVersion,
			});
		}
	}, [traceId, promptVersion, analyticsFlow]);

	// Populate common analytics attributes
	useEffect(() => {
		if (!configItem) {
			return;
		}

		const backendModel = configItem.getBackendModel?.();
		const autoAcceptAIChanges = canInlineStreamChanges();
		const isInvokedFromEmptyPage = isEmptyDocument(editorView.state.doc);
		const isSmartLinkToolbarExpEnabled = expValEqualsNoExposure(
			'platform_editor_rovobutton_smartlink_toolbar_exp',
			'isEnabled',
			true,
		);

		analyticsFlow.updateCommonAttributes({
			backendModel,
			...analyticsFlow.getUserFlowCommonAttributes(configItem),
			autoAcceptAIChanges,
			inlineStreamingAvailable: true,
			isInvokedFromEmptyPage,
			...(isSmartLinkToolbarExpEnabled &&
				(decorationState?.has3pSources !== undefined ||
					decorationState?.sourceProduct !== undefined) && {
					has3pSources: decorationState?.has3pSources,
					sourceProduct: decorationState?.sourceProduct,
				}),
		});

		if (initialPositions) {
			const selectionCategory = getSelectionCategory(editorView, initialPositions);
			const { charCount, wordCount } = rangeCharWordCount(
				editorView.state.doc,
				initialPositions[0],
				initialPositions[1],
			);

			analyticsFlow.updateCommonAttributes({
				selectionCharCount: charCount,
				selectionWordCount: wordCount,
				selectionCategory,
			});

			const selectionNodes = getSelectionNodeTypes(editorView.state, initialPositions);
			analyticsFlow.updateCommonAttributes({ selectionNodes });
		}
	}, [
		analyticsFlow,
		configItem,
		initialPositions,
		editorView,
		canInlineStreamChanges,
		decorationState?.has3pSources,
		decorationState?.sourceProduct,
	]);

	const [pendingStepVersion, setPendingStepVersion] = useState(0);

	const fireAnalyticsStep = useCallback<FireAnalyticsStep>((step, targetStep, attributes) => {
		if (lastQueuedStep.current !== step) {
			lastQueuedStep.current = step;
			pendingStepRef.current = { step, targetStep, attributes };
			setPendingStepVersion((v) => v + 1);
		}
	}, []);

	useEffect(() => {
		const pendingStep = pendingStepRef.current;
		if (!pendingStep) {
			return;
		}
		pendingStepRef.current = null;

		const { step, targetStep, attributes } = pendingStep;

		// Inject the prompt step into the invoke→loading chain when we came
		// through the prompt screen (replicates: invoke → prompt → loading).
		if (step === 'invoke' && lastFiredSessionStatus.current === 'prompt') {
			lastFiredSessionStatus.current = 'invoke-after-prompt';

			analyticsFlow.fireQueuedStep('prompt', CALLER_COMPONENT);
			analyticsFlow.enqueueStep(
				{
					stepName: 'prompt',
					attributes: {
						experienceName: configItem?.key,
						featureId: configItem?.featureId,
						promptTitle: configItem?.promptLabel
							? intl.formatMessage(configItem.promptLabel)
							: undefined,
						promptType: 'generate',
						usingCommandPalette: true,
					},
				},
				CALLER_COMPONENT,
			);

			analyticsFlow.fireQueuedStep(targetStep, CALLER_COMPONENT);

			return;
		}

		// invoke is already queued on reset
		if (step !== 'invoke') {
			analyticsFlow.enqueueStep({ stepName: step, attributes }, CALLER_COMPONENT);
		}

		// Fire the unified ACTION.VIEWED event when the stream has completed.
		// In the legacy path, this was handled by useInEditorDirectStreaming on isFinalStream.
		// In the pure-PM path (xstate migration), the streaming happens in ProseMirror plugins
		// without React. handleFinalize fires 'complete' after the stream ends, so we fire
		// ACTION.VIEWED here when the bridge processes the 'complete' step.
		if (step === 'complete' && !hasViewedFired.current) {
			const unifiedEventData = analyticsFlow.getUnifiedEventData();
			const isSmartLinkToolbarExpEnabled = expValEqualsNoExposure(
				'platform_editor_rovobutton_smartlink_toolbar_exp',
				'isEnabled',
				true,
			);
			if (
				unifiedEventData.invokedFrom !== 'streamingApi' &&
				unifiedEventData.invokedFrom !== 'confluenceTitleGenOnPaste'
			) {
				hasViewedFired.current = true;
				fireAIAnalyticsEvent({
					payload: createUnifiedAnalyticsPayload({
						action: ACTION.VIEWED,
						interactionID: unifiedEventData.interactionID,
						experienceName: unifiedEventData.experienceName,
						isProactive: false,
						invokedFrom: unifiedEventData.invokedFrom,
						...(isSmartLinkToolbarExpEnabled && {
							has3pSources: unifiedEventData.has3pSources,
							sourceProduct: unifiedEventData.sourceProduct,
						}),
						attributes: unifiedEventData.attributes,
						traceIds: unifiedEventData.traceIds,
					}),
				});
			}
		}

		analyticsFlow.fireQueuedStep(targetStep, CALLER_COMPONENT);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pendingStepVersion, analyticsFlow, fireAIAnalyticsEvent, configItem, intl]);

	// Expose fireAnalyticsStep to the pure-pm plugin via the plugin API (ref so plugin always calls current callback)
	const bridgeRef = React.useRef<AnalyticsFlowBridgeRef['current']>(null);
	useEffect(() => {
		bridgeRef.current = { fireAnalyticsStep };
		api?.aiExperience?.actions?.setAnalyticsBridgeRef?.(bridgeRef);
		return () => {
			api?.aiExperience?.actions?.setAnalyticsBridgeRef?.(null);
			bridgeRef.current = null;
		};
	}, [api, fireAnalyticsStep]);

	return <>{children}</>;
}
